<template>
  <span
    class="post-audit-score-badge"
    :class="[
      `post-audit-score-badge--${scoreLabel}`,
      `post-audit-score-badge--${size}`,
      {
        'post-audit-score-badge--achieved': achieved,
        'post-audit-score-badge--muted': mutedWhenInactive && !achieved
      }
    ]"
  >
    {{ label }}
  </span>
</template>

<script setup lang="ts">
import type { GeminiAuditSlideViewModel } from '~/composables/useGeminiAuditCarousel';

const props = withDefaults(
  defineProps<{
    scoreLabel: GeminiAuditSlideViewModel['scoreLabel'];
    size?: 'sm' | 'md';
    achieved?: boolean;
    mutedWhenInactive?: boolean;
  }>(),
  {
    size: 'md',
    achieved: false,
    mutedWhenInactive: false
  }
);

const { t } = useI18n();

const label = computed(() =>
  t(`admin.qstashRapidApiPoc.postInspector.carousel.scoreLabels.${props.scoreLabel}`)
);
</script>

<style scoped>
.post-audit-score-badge {
  align-items: center;
  background: transparent;
  border: 1px solid var(--audit-score-border);
  border-radius: var(--g-radius-full);
  color: var(--audit-score-text);
  display: inline-flex;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    sans-serif;
  font-weight: 650;
  line-height: 1;
  text-transform: capitalize;
  white-space: nowrap;
}

.post-audit-score-badge--sm {
  font-size: 11px;
  padding: 6px 9px;
}

.post-audit-score-badge--md {
  font-size: 12px;
  padding: 8px 12px;
}

.post-audit-score-badge--achieved {
  background: var(--audit-score-bg);
}

.post-audit-score-badge--great {
  --audit-score-bg: rgba(126, 231, 135, 0.14);
  --audit-score-border: rgba(126, 231, 135, 0.42);
  --audit-score-glow: rgba(126, 231, 135, 0.12);
  --audit-score-ring: rgba(126, 231, 135, 0.18);
  --audit-score-text: var(--g-positive-ink);
}

.post-audit-score-badge--good {
  --audit-score-bg: var(--g-accent-soft);
  --audit-score-border: var(--g-accent-stroke);
  --audit-score-glow: rgba(71, 221, 176, 0.12);
  --audit-score-ring: rgba(71, 221, 176, 0.18);
  --audit-score-text: var(--g-accent-ink);
}

.post-audit-score-badge--fair {
  --audit-score-bg: rgba(217, 164, 65, 0.14);
  --audit-score-border: rgba(217, 164, 65, 0.42);
  --audit-score-glow: rgba(217, 164, 65, 0.1);
  --audit-score-ring: rgba(217, 164, 65, 0.18);
  --audit-score-text: var(--g-warning-ink);
}

.post-audit-score-badge--needs_work {
  --audit-score-bg: rgba(239, 111, 108, 0.14);
  --audit-score-border: rgba(239, 111, 108, 0.45);
  --audit-score-glow: rgba(239, 111, 108, 0.12);
  --audit-score-ring: rgba(239, 111, 108, 0.2);
  --audit-score-text: var(--g-negative-ink);
}

/* cas-10e2: unselected pills sit on the themed inset well. The old fixed dark
   slab (rgba(31, 34, 40, 0.72)) read 2.85:1 on a white light-mode card.
   Secondary ink, not tertiary: tertiary on inset is just under AA in light. */
.post-audit-score-badge--muted {
  --audit-score-border: var(--g-border-subtle);
  --audit-score-text: var(--g-text-secondary);
  background: var(--g-bg-inset);
}
</style>
